// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { useLocation, useParams } from "react-router-dom";

import { parsePlatformRef } from "../operation/platformRef";
import { JUDGE_POSITIONS, JudgePosition } from "./resolveJudgeVotes";

import styles from "./IntegratedJudgesPanelRoute.module.scss";

const CANVAS_WIDTH = 1400;
const CANVAS_HEIGHT = 1120;

const JUDGE_LABELS: Record<JudgePosition, string> = {
  J1: "Juez Lateral Izquierdo",
  J2: "Juez Central",
  J3: "Juez Lateral Derecho",
};

const AndroidStatusBar = () => {
  const time = new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());

  return (
    <div className={styles.statusBar} aria-hidden="true">
      <span className={styles.deviceTime}>{time}</span>
      <span className={styles.camera} />
      <span className={styles.statusIcons}>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path d="M8 13.3L.67 5.97a10.37 10.37 0 0114.66 0L8 13.3z" />
        </svg>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path d="M14.67 14.67V1.33L1.33 14.67h13.34z" />
        </svg>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <rect x="3.75" y="2" width="8.5" height="13" rx="1.5" />
          <rect x="5.5" y="0.9" width="5" height="2" rx="0.5" />
        </svg>
      </span>
    </div>
  );
};

const AndroidDevice = ({ title, src }: { title: string; src: string }) => (
  <div className={styles.device}>
    <AndroidStatusBar />
    <iframe className={styles.judgeFrame} src={src} title={title} />
    <div className={styles.navigationBar} aria-hidden="true">
      <span />
    </div>
  </div>
);

const IntegratedJudgesPanelRoute = () => {
  const params = useParams();
  const location = useLocation();
  const platformRef = parsePlatformRef(params.day, params.platformId);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const canvasWrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const stage = stageRef.current;
    const canvasWrap = canvasWrapRef.current;
    if (stage === null || canvasWrap === null) {
      return;
    }

    const fit = () => {
      const width = stage.clientWidth || window.innerWidth;
      const height = stage.clientHeight || window.innerHeight;
      const scale = Math.min(width / CANVAS_WIDTH, height / CANVAS_HEIGHT, 1);
      canvasWrap.style.transform =
        `translate(${-CANVAS_WIDTH * scale * 0.5}px, ${-CANVAS_HEIGHT * scale * 0.5}px) scale(${scale})`;
    };

    fit();
    window.addEventListener("resize", fit);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            fit();
          });
    resizeObserver?.observe(stage);

    return () => {
      window.removeEventListener("resize", fit);
      resizeObserver?.disconnect();
    };
  }, []);

  if (platformRef === null) {
    return (
      <main className={styles.invalid} role="alert">
        <h1>Panel Integrado de Jueces inválido</h1>
        <p>El día o la plataforma no son válidos.</p>
      </main>
    );
  }

  return (
    <main ref={stageRef} className={styles.stage} aria-label="Panel Integrado de Jueces">
      <div ref={canvasWrapRef} className={styles.canvasWrap}>
        <div className={styles.canvas}>
          <header className={styles.titleRow}>
            <span>WRPF Argentina · Mesa de Jurados</span>
            <h1>Jurado — 3 Jueces</h1>
          </header>
          <div className={styles.phones}>
            {JUDGE_POSITIONS.map((position) => (
              <AndroidDevice
                key={position}
                title={JUDGE_LABELS[position]}
                src={`/days/${platformRef.day}/platforms/${platformRef.platformId}/judges/${position}${location.search}`}
              />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default IntegratedJudgesPanelRoute;
